import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import { Upload, Link as LinkIcon, AlertCircle, Video, Image } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export function NewVideoPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [url, setUrl] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [productId, setProductId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [uploadMethod, setUploadMethod] = useState<'url' | 'file'>('url')
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [uploadProgress, setUploadProgress] = useState(0)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const [products, setProducts] = useState<any[]>([])

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    
    // Fetch user's products for the dropdown
    if (user.role === 'seller') {
      fetchUserProducts()
    }
  }, [user, navigate])

  const fetchUserProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, title')
        .eq('seller_id', user?.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProducts(data || [])
    } catch (err) {
      console.error('Error fetching products:', err)
    }
  }

  if (!user) {
    return null
  }

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      // Check if file is a video
      if (!file.type.startsWith('video/')) {
        setError('Please upload a valid video file')
        return
      }
      // Check file size (limit to 500MB)
      if (file.size > 500 * 1024 * 1024) {
        setError('Video file size must be less than 500MB')
        return
      }
      setVideoFile(file)
      setError('')
    }
  }

  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        setError('Please upload a valid image file for the thumbnail')
        return
      }
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Thumbnail file size must be less than 5MB')
        return
      }
      setThumbnailFile(file)
      setError('')
    }
  }

  const uploadVideoFile = async (): Promise<string> => {
    if (!videoFile || !user) return ''

    const fileExt = videoFile.name.split('.').pop()
    const fileName = `${Date.now()}.${fileExt}`
    const filePath = `${user.id}/${fileName}`

    const maxRetries = 3;
    let retryCount = 0;
    let uploadError: any = null;

    while (retryCount < maxRetries) {
      try {
        retryCount++;
        console.log(`Attempting video upload (attempt ${retryCount}/${maxRetries})`);

        const { error, data } = await supabase.storage
          .from('videos')
          .upload(filePath, videoFile, {
            cacheControl: '3600',
            upsert: false,
            onUploadProgress: (progress) => {
              const percentage = (progress.loaded / progress.total) * 100;
              setUploadProgress(Math.round(percentage));
            },
          });

        if (error) {
          uploadError = error;
          console.warn(`Video upload attempt ${retryCount} failed:`, error);
          // Exponential backoff: wait 2^retryCount seconds
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          continue; // Retry
        }

        const { data: { publicUrl } } = supabase.storage
          .from('videos')
          .getPublicUrl(filePath);

        console.log('Video upload successful');
        return publicUrl;

      } catch (err: any) {
        uploadError = err;
        console.warn(`Video upload attempt ${retryCount} experienced an exception:`, err);
        // Exponential backoff: wait 2^retryCount seconds
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
      }
    }

    // If all retries failed
    console.error('Video upload failed after multiple retries:', uploadError);
    throw new Error(`Video upload failed after multiple retries: ${uploadError?.message || 'Unknown error'}`);
  }

  const uploadThumbnailFile = async (): Promise<string> => {
    if (!thumbnailFile || !user) return ''

    const fileExt = thumbnailFile.name.split('.').pop()
    const fileName = `${Date.now()}.${fileExt}`
    const filePath = `${user.id}/${fileName}`

    const maxRetries = 3;
    let retryCount = 0;
    let uploadError: any = null;

    while (retryCount < maxRetries) {
      try {
        retryCount++;
        console.log(`Attempting thumbnail upload (attempt ${retryCount}/${maxRetries})`);

        const { error, data } = await supabase.storage
          .from('thumbnails')
          .upload(filePath, thumbnailFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          uploadError = error;
          console.warn(`Thumbnail upload attempt ${retryCount} failed:`, error);
          // Exponential backoff: wait 2^retryCount seconds
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          continue; // Retry
        }

        const { data: { publicUrl } } = supabase.storage
          .from('thumbnails')
          .getPublicUrl(filePath);

        console.log('Thumbnail upload successful');
        return publicUrl;

      } catch (err: any) {
        uploadError = err;
        console.warn(`Thumbnail upload attempt ${retryCount} experienced an exception:`, err);
        // Exponential backoff: wait 2^retryCount seconds
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
      }
    }

    // If all retries failed
    console.error('Thumbnail upload failed after multiple retries:', uploadError);
    return "";
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    setUploadProgress(0)

    try {
      let videoUrl = url
      let thumbnailImageUrl = thumbnailUrl

      // If upload method is file, upload the files
      if (uploadMethod === 'file') {
        if (!videoFile) {
          setError('Please select a video file to upload')
          setLoading(false)
          return
        }

        // Upload video file
        try {
          videoUrl = await uploadVideoFile()
        } catch (err: any) {
          console.error('Error uploading video:', err)
          setError(`Error uploading video: ${err.message || 'Unknown error'}`)
          setLoading(false)
          return
        }

        // Upload thumbnail if provided
        if (thumbnailFile) {
          try {
            thumbnailImageUrl = await uploadThumbnailFile()
          } catch (err) {
            console.error('Error uploading thumbnail:', err)
            // Continue even if thumbnail upload fails
          }
        }
      } else {
        // URL method validation
        if (!url) {
          setError('Please enter a video URL')
          setLoading(false)
          return
        }
      }

      // Insert video record
      const { error: uploadError } = await supabase.from('videos').insert([
        {
          user_id: user.id,
          title,
          description,
          video_url: videoUrl,
          thumbnail_url: thumbnailImageUrl,
          product_id: productId || null,
          status: 'pending',
          likes_count: 0,
        },
      ])

      if (uploadError) throw uploadError

      // Show success toast
      setToastMessage('Video uploaded successfully! It will be reviewed before publishing.')
      setToastType('success')
      setShowToast(true)

      // Redirect after a delay
      setTimeout(() => {
        navigate('/videos')
      }, 3000)
    } catch (err: any) {
      console.error('Error uploading video:', err)
      setError(`Error uploading video: ${err.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      
      <div className="mb-8">
        <BackButton to="/videos" label="Retour aux vidéos" />
      </div>
      
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Upload New Video
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Share your product reviews and demonstrations with the community
          </p>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-2 border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Video Title *
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="block w-full border-2 border-black rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1">
                <textarea
                  id="description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="block w-full border-2 border-black rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Describe your video content..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Method
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setUploadMethod('url')}
                  className={`flex flex-col items-center p-4 border-2 rounded-lg ${
                    uploadMethod === 'url'
                      ? 'border-primary bg-primary/5'
                      : 'border-black hover:border-primary'
                  }`}
                >
                  <LinkIcon className={`h-8 w-8 ${
                    uploadMethod === 'url' ? 'text-primary' : 'text-gray-400'
                  }`} />
                  <span className="mt-2 text-sm font-medium text-gray-900">URL Link</span>
                  <span className="mt-1 text-xs text-gray-500">Paste a video URL</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setUploadMethod('file')}
                  className={`flex flex-col items-center p-4 border-2 rounded-lg ${
                    uploadMethod === 'file'
                      ? 'border-primary bg-primary/5'
                      : 'border-black hover:border-primary'
                  }`}
                >
                  <Upload className={`h-8 w-8 ${
                    uploadMethod === 'file' ? 'text-primary' : 'text-gray-400'
                  }`} />
                  <span className="mt-2 text-sm font-medium text-gray-900">File Upload</span>
                  <span className="mt-1 text-xs text-gray-500">Upload from your device</span>
                </button>
              </div>
            </div>

            {uploadMethod === 'url' ? (
              <>
                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                    Video URL *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="url"
                      id="url"
                      required={uploadMethod === 'url'}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="block w-full pl-10 border-2 border-black rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="https://example.com/video"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Enter the URL of your video from YouTube, Vimeo, or other platforms
                  </p>
                </div>

                <div>
                  <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700">
                    Thumbnail URL (Optional)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="url"
                      id="thumbnail"
                      value={thumbnailUrl}
                      onChange={(e) => setThumbnailUrl(e.target.value)}
                      className="block w-full pl-10 border-2 border-black rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="https://example.com/thumbnail.jpg"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Video File *
                  </label>
                  <div className="mt-1">
                    <div className="relative border-2 border-black border-dashed rounded-lg hover:border-primary transition-colors">
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        className="sr-only"
                        onChange={handleVideoFileChange}
                        required={uploadMethod === 'file'}
                      />
                      <label
                        htmlFor="video-upload"
                        className="cursor-pointer w-full h-full flex flex-col items-center justify-center p-6"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        {videoFile ? (
                          <>
                            <Video className="h-10 w-10 text-primary" />
                            <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                              {videoFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </>
                        ) : (
                          <>
                            <Video className="h-10 w-10 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">
                              Click to upload video (max 500MB)
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              MP4, MOV, WEBM formats supported
                            </p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Thumbnail Image (Optional)
                  </label>
                  <div className="mt-1">
                    <div className="relative border-2 border-black border-dashed rounded-lg hover:border-primary transition-colors">
                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleThumbnailFileChange}
                      />
                      <label
                        htmlFor="thumbnail-upload"
                        className="cursor-pointer w-full h-full flex flex-col items-center justify-center p-6"
                        onClick={() => thumbnailInputRef.current?.click()}
                      >
                        {thumbnailFile ? (
                          <>
                            <div className="h-24 w-24 relative">
                              <img
                                src={URL.createObjectURL(thumbnailFile)}
                                alt="Thumbnail preview"
                                className="h-full w-full object-cover rounded"
                              />
                            </div>
                            <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                              {thumbnailFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(thumbnailFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </>
                        ) : (
                          <>
                            <Image className="h-10 w-10 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">
                              Click to upload thumbnail (max 5MB)
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              JPG, PNG, WEBP formats recommended
                            </p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {loading && uploadProgress > 0 && (
                  <div className="mt-2">
                    <div className="text-sm text-gray-700 mb-1">Uploading: {uploadProgress}%</div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 border border-black">
                      <div 
                        className="bg-primary h-2.5 rounded-full" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <label htmlFor="product" className="block text-sm font-medium text-gray-700">
                Featured Product (Optional)
              </label>
              <div className="mt-1">
                <select
                  id="product"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  className="block w-full border-2 border-black rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.title}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Link this video to one of your products
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                className="mr-3 border-2 border-black"
                onClick={() => navigate('/videos')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="inline-flex items-center border-2 border-black"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Upload className="h-5 w-5 mr-2" />
                )}
                Upload Video
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}